import {
  DeesElement,
  property,
  state,
  html,
  type TemplateResult,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { DeesServiceLibLoader, type IEchartsBundle, type IEchartsInstance } from '../../services/index.js';
import { getEchartsThemeOptions } from './dees-chart-echarts-theme.js';
import '../00group-layout/dees-tile/dees-tile.js';

/**
 * Abstract base class for ECharts-based chart components.
 * Handles library loading, chart lifecycle, resize observation, and theme switching.
 * Subclasses implement `buildOption()` to define their chart configuration.
 */
export abstract class DeesChartEchartsBase extends DeesElement {
  @property()
  accessor label: string = 'Untitled Chart';

  /** Drop the chart's outer frame: the host already owns the surface. */
  @property({ type: Boolean, reflect: true })
  accessor frameless: boolean = false;

  protected chartInstance: IEchartsInstance | null = null;
  protected echartsBundle: IEchartsBundle | null = null;
  private resizeObserver: ResizeObserver | null = null;
  @state() private accessor loading = true;
  @state() private accessor loadError = '';
  @state() private accessor hiddenSeries = new Set<string>();
  protected legendItems(): Array<{name: string; color: string; value?: string}> { return []; }
  protected reducedMotion = false;
  protected chartWidth = 0;
  protected hasChartData(): boolean { return true; }
  protected dataSummary(): string { return this.label; }

  constructor() {
    super();
    domtools.elementBasic.setup();
    this.registerStartupFunction(async (signal) => {
      this.loading = true; this.loadError = '';
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const updateMotion = () => { this.reducedMotion = motion.matches; this.updateChart(); };
      this.reducedMotion = motion.matches;
      motion.addEventListener('change', updateMotion, { signal });
      await this.updateComplete;
      try {
        this.echartsBundle = await DeesServiceLibLoader.getInstance().loadEcharts();
        if (signal.aborted) return;
        const container = this.shadowRoot!.querySelector<HTMLDivElement>('.chartContainer')!;
        this.chartWidth = container.clientWidth;
        this.chartInstance = this.echartsBundle.init(container, null, { renderer: 'svg' });
        this.updateChart();
        this.resizeObserver = new ResizeObserver(([entry]) => {
          if (signal.aborted) return;
          const previousWidth = this.chartWidth;
          this.chartWidth = entry.contentRect.width;
          this.chartInstance?.resize();
          if ((previousWidth < 480) !== (this.chartWidth < 480)) this.updateChart();
        });
        this.resizeObserver.observe(container);
      } catch (error) {
        if (!signal.aborted) this.loadError = 'The chart could not be loaded.';
        console.error('Failed to initialize ECharts:', error);
      } finally {
        if (!signal.aborted) this.loading = false;
      }
    });
    this.registerGarbageFunction(async () => {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.chartInstance) {
        try {
          this.chartInstance.dispose();
          this.chartInstance = null;
        } catch (e) {
          console.error('Error disposing ECharts instance:', e);
        }
      }
    });
  }

  public render(): TemplateResult { return this.renderChartFrame(); }

  public renderChartFrame(): TemplateResult {
    const message = this.loadError || (this.loading ? 'Loading chart…' : !this.hasChartData() ? 'No data for this period' : '');
    return html`<dees-tile .frameless=${this.frameless}>
      <div slot="header" class="chartHeader"><h2 class="chartLabel">${this.label}</h2></div>
      <div class="chartContainer" aria-hidden="true"></div>
      <p class="chartSummary">${this.dataSummary()}</p>
      ${this.legendItems().length ? html`<div slot="footer" class="chartLegend" role="group" aria-label="Chart series">${this.legendItems().map(item => html`
        <button type="button" aria-pressed=${!this.hiddenSeries.has(item.name)} @click=${() => {
          const hidden = new Set(this.hiddenSeries);
          if (hidden.has(item.name)) hidden.delete(item.name); else hidden.add(item.name);
          this.hiddenSeries = hidden; this.updateChart();
        }}><span class="legendColor" style=${`background: ${item.color}`}></span><span>${item.name}</span>${item.value !== undefined ? html`<strong>${item.value}</strong>` : ''}</button>
      `)}</div>` : ''}
      ${message ? html`<div class="chartState" role="status">${message}</div>` : ''}
    </dees-tile>`;
  }

  public async updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    if (changedProperties.has('goBright') && this.chartInstance) {
      this.applyTheme();
    }
  }

  protected abstract buildOption(): Record<string, any>;

  protected updateChart(): void {
    if (!this.chartInstance) return;
    const themeOptions = getEchartsThemeOptions(this.goBright);
    const chartOption = this.buildOption();
    // Deep-merge theme defaults with chart-specific options for nested objects
    const merged = {
      ...themeOptions,
      ...chartOption,
      animation: !this.reducedMotion,
      animationDuration: 250,
      animationDurationUpdate: 200,
      textStyle: { ...themeOptions.textStyle, ...(chartOption.textStyle || {}) },
      tooltip: { ...themeOptions.tooltip, ...(chartOption.tooltip || {}) },
      legend: {
        ...themeOptions.legend,
        ...(chartOption.legend || {}),
        show: false,
        selected: Object.fromEntries(this.legendItems().map(item => [item.name, !this.hiddenSeries.has(item.name)])),
        textStyle: { ...(themeOptions.legend?.textStyle || {}), ...(chartOption.legend?.textStyle || {}) },
      },
    };
    this.chartInstance.setOption(merged, true);
  }

  protected applyTheme(): void {
    this.updateChart();
  }

  public async forceResize(): Promise<void> {
    this.chartInstance?.resize();
  }
}
